import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  getNextPage,
  selectAddingMessageEvents,
  selectAllNotificationMessages,
} from './notification-messages.selectors';
import * as notificationMessageActions from './notification-messages.actions';
import { ExcludableList, MessageEventType } from '@boma/shared/entities';
import { BehaviorSubject, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

import { NotificationsApiService } from '../../../../../shared/services/src/lib/notifications/notifications-api.service';
import { getSelectedOrganizationInfo } from '@boma/supplier-portal/shared/state';
import { LoggingService } from '@boma/supplier-portal/shared/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FirebaseNotificationsService } from 'libs/shared/common/src/lib/services/firebase-notifications.service';

@Injectable()
export class NotificationMessagesFacade implements OnDestroy {
  messages$ = this.store.select(selectAllNotificationMessages);
  addingMessageEvents$ = this.store.select(selectAddingMessageEvents);
  unreadMessageCount$ = new BehaviorSubject<number>(null);
  nextPage$ = this.store.select(getNextPage);

  private readonly _unsubscribe = new Subject();

  constructor(
    private store: Store,
    private firebaseNotificationsService: FirebaseNotificationsService,
    private notificationsApiService: NotificationsApiService,
    private loggingService: LoggingService
  ) {
    this.loadNotifications({
      page: 1,
      pageSize: 25,
      messageEventTypes: new ExcludableList<MessageEventType>(),
      refresh: false,
    });
    this._loadUnreadNotifications().subscribe();
    this.firebaseNotificationsService.messages$
      .pipe(takeUntilDestroyed())
      .subscribe((messages) => loggingService.logDebug('Messages incoming...'));
    this.firebaseNotificationsService.messages$
      .pipe(
        takeUntil(this._unsubscribe),
        switchMap(() => {
          return this._loadUnreadNotifications();
        })
      )
      .subscribe();
  }

  loadNotifications(params: {
    page: number;
    pageSize: number;
    messageEventTypes: ExcludableList<MessageEventType>;
    refresh: boolean;
  }) {
    const { page, pageSize, messageEventTypes, refresh } = params;
    this.store.dispatch(
      notificationMessageActions.loadNotificationMessages({
        page: page,
        pageSize: pageSize,
        messageEventTypes: messageEventTypes,
        refresh: refresh || false,
      })
    );
  }

  addEventsToMessage(
    messages: {
      messageId: string;
      eventType: MessageEventType;
    }[]
  ) {
    this.store.dispatch(
      notificationMessageActions.addNotificationMessagesEvents({ messages })
    );
  }

  loadLatestMessages() {
    this.store.dispatch(notificationMessageActions.loadLatestMessages());
  }

  ngOnDestroy(): void {
    this._unsubscribe.next('');
    this._unsubscribe.complete();
  }

  private _loadUnreadNotifications() {
    return this.store.select(getSelectedOrganizationInfo).pipe(
      switchMap((org) => {
        return this.notificationsApiService
          .listNotifications(org.id, 1, 25, {
            messageEventTypes: new ExcludableList<MessageEventType>([
              { include: false, item: MessageEventType.Read },
            ]),
          })
          .pipe(
            tap((messages) => {
              this.loggingService.logDebug(
                'Loaded unread notifications',
                messages
              );
              this.unreadMessageCount$.next(messages.totalItems);
            })
          );
      })
    );
  }
}
